import { api } from '../api/http';

export type PushState = 'unsupported' | 'needs-install' | 'denied' | 'enabled' | 'disabled' | 'server-disabled';

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

function urlBase64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function registration() {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

/** The chat was opened: its message and missed-call notifications are no longer news. */
export async function clearChatNotifications(conversationId: string) {
  try {
    const reg = await registration();
    const list = (await reg?.getNotifications()) ?? [];
    for (const n of list) if (n.tag === `conv:${conversationId}` || n.tag === `missed:${conversationId}`) n.close();
  } catch {
    /* notifications unsupported */
  }
}

export async function pushState(): Promise<PushState> {
  if (!('PushManager' in window) || !('Notification' in window)) {
    // iOS exposes Web Push only to PWAs added to the Home Screen (iOS 16.4+).
    return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  return sub && Notification.permission === 'granted' ? 'enabled' : 'disabled';
}

/**
 * Must be called directly from a click handler. Safari (iOS) only shows the permission prompt while the
 * user gesture is still active, so the permission is requested first, before any network request.
 */
export async function enablePush(): Promise<PushState> {
  const permissionP = Notification.requestPermission();
  const keyP = api<{ key: string | null }>('GET', '/api/push/vapid-public-key');
  const permission = await permissionP;
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'disabled';
  const { key } = await keyP;
  if (!key) return 'server-disabled';
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
  }
  await api('POST', '/api/push/subscriptions', sub.toJSON());
  return 'enabled';
}

export async function unsubscribePush() {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await api('DELETE', '/api/push/subscriptions', { endpoint: sub.endpoint }).catch(() => {});
  await sub.unsubscribe();
}

/** Re-sends an existing subscription after login so it is bound to the current session. */
export async function refreshPushSubscription() {
  if (!('PushManager' in window) || Notification.permission !== 'granted') return;
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) await api('POST', '/api/push/subscriptions', sub.toJSON()).catch(() => {});
}
